import { gql } from '@apollo/client';

import {
  BoolOrgPref,
  DurationInput,
  DurationOrgPref,
  IntOrgPref,
  JsonOrgPref,
  OrgJsonPref,
  PrefSet,
  StringOrgPref,
  useUpdateOrgPrefsMutation,
} from 'src/generated/mloop-graphql';

import { OrgPrefName } from 'src/utils/api/org';
import { removeTypename } from 'src/utils/removeTypename';

import { OrgPrefsFragment } from './get';
import { AllOrgPrefName } from './types';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GQL_ORG_PREFS_UPDATE_MUTATION = gql`
  ${OrgPrefsFragment}

  mutation UpdateOrgPrefs($input: OrgPrefUpdateInput!) {
    orgPrefUpdate(input: $input) {
      orgPref {
        ...OrgPrefsData
      }
    }
  }
`;

function useSetOrgPref(onCompleted?: () => void) {
  const [update] = useUpdateOrgPrefsMutation({ onCompleted });

  const updateOrgPrefs = (prefName: AllOrgPrefName | OrgPrefName, prefValue: PrefSet) => {
    return update({
      variables: {
        input: {
          prefName,
          set: prefValue,
        },
      },
      onCompleted,
    });
  };

  return { updateOrgPrefs };
}

export function useSetOrgPrefBool(prefName: OrgPrefName, onCompleted?: () => void) {
  const { updateOrgPrefs } = useSetOrgPref(onCompleted);
  return (value: boolean, isLocked?: boolean) => {
    const input: PrefSet = { boolValue: value };
    if (isLocked !== undefined) {
      input.isLocked = isLocked;
    }
    return updateOrgPrefs(prefName, input);
  };
}

export function useSetOrgPrefInt(prefName: OrgPrefName | IntOrgPref, onCompleted?: () => void) {
  const { updateOrgPrefs } = useSetOrgPref(onCompleted);
  return (value: number, isLocked?: boolean) => {
    const input: PrefSet = { intValue: value };
    if (isLocked !== undefined) {
      input.isLocked = isLocked;
    }
    return updateOrgPrefs(prefName, input);
  };
}

export function useSetOrgPrefString(prefName: OrgPrefName | StringOrgPref, onCompleted?: () => void) {
  const { updateOrgPrefs } = useSetOrgPref(onCompleted);
  return (value: string, isLocked?: boolean) => {
    const input: PrefSet = { stringValue: value };
    if (isLocked !== undefined) {
      input.isLocked = isLocked;
    }
    return updateOrgPrefs(prefName, input);
  };
}

export function useSetOrgPrefStringArray(prefName: OrgPrefName, onCompleted?: () => void) {
  const { updateOrgPrefs } = useSetOrgPref(onCompleted);
  return (value: string[] | null, isLocked?: boolean) => {
    const input: PrefSet = {
      stringArray: value || [],
    };
    if (isLocked !== undefined) {
      input.isLocked = isLocked;
    }
    return updateOrgPrefs(prefName, input);
  };
}

export function useSetOrgPrefJson(prefName: OrgPrefName, onCompleted?: () => void) {
  const { updateOrgPrefs } = useSetOrgPref(onCompleted);
  return (value: unknown, isLocked?: boolean) => {
    const input: PrefSet = { json: JSON.stringify(value) };
    if (isLocked !== undefined) {
      input.isLocked = isLocked;
    }
    return updateOrgPrefs(prefName, input);
  };
}

/**
 * @param prefName
 * @returns mutationHook
 */
export function useSetOrgPrefDuration(prefName: DurationOrgPref | OrgPrefName, onCompleted?: () => void) {
  const { updateOrgPrefs } = useSetOrgPref(onCompleted);
  return (duration: DurationInput) => {
    const input: PrefSet = { duration };
    return updateOrgPrefs(prefName, input);
  };
}

export function useSetOrgPrefJsonTyped<T extends OrgJsonPref>(prefName: JsonOrgPref, onCompleted?: () => void) {
  const { updateOrgPrefs } = useSetOrgPref(onCompleted);
  return (value: T, isLocked?: boolean) => {
    const input: PrefSet = { json: JSON.stringify(removeTypename(value)) };
    if (isLocked !== undefined) {
      input.isLocked = isLocked;
    }
    return updateOrgPrefs(prefName, input);
  };
}

export function useSetOrgPrefBoolTyped(prefName: BoolOrgPref, onCompleted?: () => void) {
  const { updateOrgPrefs } = useSetOrgPref(onCompleted);
  return (value: boolean, isLocked?: boolean) => {
    const input: PrefSet = { boolValue: Boolean(value) };
    if (isLocked !== undefined) {
      input.isLocked = isLocked;
    }
    return updateOrgPrefs(prefName, input);
  };
}

export function useSetOrgPrefStringTyped(prefName: StringOrgPref, onCompleted?: () => void) {
  const { updateOrgPrefs } = useSetOrgPref(onCompleted);
  return (value: string, isLocked?: boolean) => {
    const input: PrefSet = { stringValue: value };
    if (isLocked !== undefined) {
      input.isLocked = isLocked;
    }
    return updateOrgPrefs(prefName, input);
  };
}
